import { JsonPipe } from '@angular/common'
import {
    Component,
    computed,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    input,
    OnInit,
    Output,
    signal,
    ViewChild,
} from '@angular/core'
import { NgSelectModule } from '@ng-select/ng-select'
import { PartsArenaService } from 'src/app/core/services/parts-arena.service'
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms'
import { PAManfacturer } from 'src/app/shared/interfaces/partsarena/pa-manufacturer.interface'
import { PAModel } from 'src/app/shared/interfaces/partsarena/pa-model.interface'
import { PAProductType } from 'src/app/shared/interfaces/partsarena/pa-producttype.interface'
import { filter, forkJoin, map, of, tap } from 'rxjs'
import { PAProductRange } from 'src/app/shared/interfaces/partsarena/pa-productrange.interface'
import { PAPartlistSectionRow } from 'src/app/shared/interfaces/partsarena/pa-partslistsectionrow.interface'
import { PAPartListSection } from 'src/app/shared/interfaces/partsarena/pa-partlistsection.interface'
import { InputPhoneComponent } from 'src/app/shared/components/form-elements/input-phone/input-phone.component'
import { CardComponent } from 'src/app/shared/components/card/card.component'
import { LabelComponent } from 'src/app/shared/components/form-elements/label/label.component'
import { ButtonComponent } from 'src/app/shared/components/form-elements/button/button.component'
import { rxResource, toSignal } from '@angular/core/rxjs-interop'
import { PAGraphicHotspot } from 'src/app/shared/interfaces/partsarena/pa-graphic-hotspot.interface'
import { NgbPopoverModule, NgbScrollSpyModule } from '@ng-bootstrap/ng-bootstrap'
import { ToastrService } from 'ngx-toastr'
import { InputComponent } from 'src/app/shared/components/form-elements/input/input.component'

@Component({
    selector: 'sf-parts-arena-diagram',
    imports: [
        ReactiveFormsModule,
        FormsModule,
        NgSelectModule,
        NgbScrollSpyModule,
        NgbPopoverModule,
        ButtonComponent,
        CardComponent,
        LabelComponent,
        InputComponent,
        InputPhoneComponent,
        JsonPipe,
    ],
    templateUrl: './parts-arena-diagram.component.html',
    styleUrl: './parts-arena-diagram.component.scss',
})
export class PartsArenaDiagramComponent implements OnInit {
    #fb = inject(FormBuilder)
    #partsArenaService = inject(PartsArenaService)
    #toastr = inject(ToastrService)

    @ViewChild('imgContainer') imageContainer!: ElementRef

    @Input() defaultPartGCNumber: string = ''
    @Input() showWolseleyOrderButtons = false
    @Output() onWolseleyOrderButtonClick = new EventEmitter<string>()

    gcNumberSearch = this.#fb.control<string>(this.defaultPartGCNumber)
    partFilter = this.#fb.control<string>('')

    manufacturerId = signal<number | null>(null)
    productTypeId = signal<number | null>(null)
    productRangeId = signal<number | null>(null)
    modelId = signal<number | null>(null)
    selectedPartListId = signal<number | null>(null)

    hoveredPart = signal<PAPartlistSectionRow | null>(null)

    manufacturers = toSignal(
        forkJoin([this.#partsArenaService.getFeaturedManufacturers(), this.#partsArenaService.getManufacturers()]).pipe(
            map(([featured, all]) => {
                const f = featured.map((x) => ({ ...x, featured: true }))
                const a = all.map((x) => ({ ...x, featured: false }))
                return f.concat(a)
            })
        ),
        { initialValue: [] }
    )

    productTypes = rxResource({
        request: () => ({
            manufacturerId: this.manufacturerId(),
        }),
        loader: ({ request }) => {
            return request.manufacturerId ? this.#partsArenaService.getProductTypes(request.manufacturerId) : of([])
        },
    })

    productRanges = rxResource({
        request: () => ({
            manufacturerId: this.manufacturerId(),
            productTypeId: this.productTypeId(),
        }),
        loader: ({ request }) => {
            if (request.manufacturerId && request.productTypeId) {
                return this.#partsArenaService.getProductRanges(request.manufacturerId, request.productTypeId)
            }
            return of([])
        },
    })

    models = rxResource({
        request: () => ({
            manufacturerId: this.manufacturerId(),
            productTypeId: this.productTypeId(),
            productRangeId: this.productRangeId(),
        }),
        loader: ({ request }) => {
            if (request.manufacturerId && request.productTypeId && request.productRangeId) {
                return this.#partsArenaService.getModels(
                    request.manufacturerId,
                    request.productTypeId,
                    request.productRangeId
                )
            }
            return of([])
        },
    })

    partList = rxResource({
        request: () => this.modelId(),
        loader: ({ request }) => {
            if (!request) {
                return of([])
            }

            return this.#partsArenaService.getPartsListSectionsForModel(request).pipe(
                map((x) => x.flatMap((y) => y.partslistSections)),
                filter((x) => x.filter((y) => y.description).length > 0),
                tap((data) => {
                    this.selectPartList(data[0])
                })
            )
        },
    })

    sections = rxResource({
        request: () => this.selectedPartListId(),
        loader: ({ request }) => {
            return request ? this.#partsArenaService.getPartlistSectionRowsForSection(request) : of([])
        },
    })

    sectionRowsFilterText = toSignal(this.partFilter.valueChanges, { initialValue: '' })
    sectionRowsFiltered = computed(() => {
        const sectionRows = this.sections.value()
        const search = this.sectionRowsFilterText()?.toLowerCase() || ''

        if (search && search !== '') {
            return sectionRows?.filter((x) => x.description.toLowerCase().includes(search))
        }

        return sectionRows
    })

    imageUrl = signal<string | null>(null)
    hotspots = signal<PAGraphicHotspot[]>([])

    selectManufacturer(manufacturer: PAManfacturer) {
        this.manufacturerId.set(manufacturer.manufacturerId)
        this.productTypeId.set(null)
        this.productRangeId.set(null)
        this.modelId.set(null)
        this.selectedPartListId.set(null)
        this.partFilter.setValue('')
    }

    selectProductType(productType: PAProductType) {
        this.productTypeId.set(productType.id)
        this.productRangeId.set(null)
        this.modelId.set(null)
        this.selectedPartListId.set(null)
        this.partFilter.setValue('')
    }

    selectProductRange(productRange: PAProductRange) {
        this.productRangeId.set(productRange.id)
        this.modelId.set(null)
        this.selectedPartListId.set(null)
        this.partFilter.setValue('')
    }

    selectModel(model: PAModel) {
        this.modelId.set(model.modelId)
        this.selectedPartListId.set(null)
        this.partFilter.setValue('')
    }

    selectPartList(partList: PAPartListSection) {
        this.selectedPartListId.set(partList.partslistSectionId)
        this.getImage(partList.partslistSectionId)
        this.partFilter.setValue('')
    }

    searchByGcNumber(): void {
        this.#partsArenaService.getByGCNumber(this.gcNumberSearch.value!).subscribe({
            next: (stuff) => {
                this.manufacturerId.set(stuff.manufacturer.manufacturerId)
                this.modelId.set(stuff.model.modelId)
                this.productRangeId.set(stuff.model.productRangeId)
                this.productTypeId.set(stuff.model.productTypeId)
                this.partFilter.setValue('')
            },
        })
    }

    getImage(value: number) {
        const width = this.imageContainer.nativeElement.offsetWidth

        const image$ = this.#partsArenaService.getPartListSectionImage(value, width)
        const hotspots$ = this.#partsArenaService.getGraphicHotspots(value, width)

        forkJoin([image$, hotspots$]).subscribe({
            next: ([image, hotspots]) => {
                this.imageUrl.set(URL.createObjectURL(image))
                this.hotspots.set(hotspots)
                console.table(hotspots)
            },
            error: () => {
                this.#toastr.error('Failed to load image')
            },
        })
    }

    onHotspotEnter(itemNumber: number): void {
        const hoveredPart = this.sections.value()?.find((x) => parseInt(x.itemNumber) === itemNumber)
        this.hoveredPart.set(hoveredPart || null)
    }

    onHotspotLeave(): void {
        this.hoveredPart.set(null)
    }

    handleWolseleyOrderButtonClick(catalogNumber: string) {
        this.onWolseleyOrderButtonClick.emit(catalogNumber)
    }

    ngOnInit() {
        if (this.defaultPartGCNumber) {
            this.gcNumberSearch.setValue(this.defaultPartGCNumber)
            this.searchByGcNumber()
        }
    }
}
